import { useCallback, useState } from 'react';
import { useMountedState } from 'react-use';

import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';
import { isAcceptedImageSearchMimeType, resizeImageToBase64 } from '~/utils/imageSearch';

export const INVALID_VISUAL_SEARCH_IMAGE_TOAST = 'Please upload a JPEG or PNG image';
export const FAILED_VISUAL_SEARCH_IMAGE_TOAST = 'Failed to process image. Please try again.';

export type UseProcessVisualSearchImageParams = {
  errorContext: string;
  onSearch: (base64Image: string) => void;
};

export const useProcessVisualSearchImage = ({ errorContext, onSearch }: UseProcessVisualSearchImageParams) => {
  const { showToast } = useToasts();
  const [isProcessing, setIsProcessing] = useState(false);
  const isMounted = useMountedState();

  const processVisualSearchImage = useCallback(
    async (file: File) => {
      if (!isAcceptedImageSearchMimeType(file.type)) {
        showToast(INVALID_VISUAL_SEARCH_IMAGE_TOAST, { color: 'red' });
        return;
      }

      setIsProcessing(true);

      try {
        const base64Image = await resizeImageToBase64(file);

        if (!isMounted()) {
          return;
        }

        onSearch(base64Image);
      } catch (error) {
        if (!isMounted()) {
          return;
        }

        showToast(FAILED_VISUAL_SEARCH_IMAGE_TOAST, { color: 'red' });
        if (isDevOrTestStage()) {
          throw error;
        } else {
          reportErrorToBugsnag({
            error,
            context: errorContext,
          });
        }
      } finally {
        if (isMounted()) {
          setIsProcessing(false);
        }
      }
    },
    [errorContext, isMounted, onSearch, showToast],
  );

  return { isProcessing, processVisualSearchImage };
};
